/**
 * Response schemas for the action admin APIs
 * (src/api/v4/admin-action-definitions.ts, src/api/v4/admin-action-types.ts).
 *
 * Both routes sent whole Prisma rows before, so each row schema lists every
 * column: no key is stripped from the wire. Timestamps accept a Date (Prisma
 * rows) or an ISO string, and a Date serializes to the same ISO string as
 * before. Json columns use PrismaJsonColumnSchema: they hold arrays, objects
 * and JSON null, so an object-only record would turn a legacy row into a 500.
 */
import { z } from 'zod';
import { DateOrIsoStringSchema } from './date-or-iso';
import { PrismaJsonColumnSchema } from './json-value';
import { ActionTypeEntrySchema } from '../view-system/action-type.schema';

export const ActionDefinitionRowSchema = z.object({
  id: z.number(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  handler: z.string(),
  handlerConfig: PrismaJsonColumnSchema,
  inputSchema: PrismaJsonColumnSchema,
  outputSchema: PrismaJsonColumnSchema,
  expectedArtifactCount: z.number(),
  blueprintKind: z.string().nullable(),
  enabled: z.boolean(),
  createdAt: DateOrIsoStringSchema,
  updatedAt: DateOrIsoStringSchema,
  menuSurface: z.string().nullable(),
});

/** One list entry: the row plus its execution count and last trigger time. */
const ActionDefinitionListEntrySchema = ActionDefinitionRowSchema.extend({
  executionCount: z.number(),
  lastTriggeredAt: DateOrIsoStringSchema.nullable(),
});
export const ActionDefinitionListResponseSchema = z.array(ActionDefinitionListEntrySchema);

/** PUT response: the row, plus `playTypeRequiresRetest: true` only when a backing play type was cleared. */
export const ActionDefinitionUpdateResponseSchema = ActionDefinitionRowSchema.extend({
  playTypeRequiresRetest: z.literal(true).optional(),
});

/** Result of the generic webhook test: `status` 0 means the request never got a response. */
export const ActionDefinitionWebhookTestResponseSchema = z.object({
  status: z.number(),
  body: z.string(),
  latencyMs: z.number(),
});

export const ActionTypeRowSchema = z.object({
  id: z.number(),
  slug: z.string(),
  label: z.string(),
  icon: z.string(),
  description: z.string(),
  category: z.string(),
  defaultInteraction: z.string(),
  isDecision: z.boolean(),
  executionLocation: z.string(),
  defaultOutcome: PrismaJsonColumnSchema,
  requiredDataFields: PrismaJsonColumnSchema,
  suggestedHandler: z.string().nullable(),
  interactionPresets: PrismaJsonColumnSchema,
  scope: z.string(),
  supportsBulk: z.boolean(),
  isRecommended: z.boolean(),
  isTerminal: z.boolean(),
  isRepeatable: z.boolean(),
  isBuiltin: z.boolean(),
  enabled: z.boolean(),
  forkedFrom: z.string().nullable(),
  createdAt: DateOrIsoStringSchema,
  updatedAt: DateOrIsoStringSchema,
});
export const ActionTypeRowListResponseSchema = z.array(ActionTypeRowSchema);

/** GET /api/v4/action-types and the registry graph: the canonical display entries. */
export const ActionTypeEntryListResponseSchema = z.array(ActionTypeEntrySchema);
